import { useState, useRef, useCallback } from 'react';

import diamondIcon from '../assets/diamond.png';

export interface FlyingPrize {
  key: string;
  id: string;
  imageUrl: string;
  top: number;
  left: number;
  targetX: number;
  targetY: number;
  animationDelay: string;
  animationDuration: string;
}

export interface FlyingDiscount {
  key: string;
  id: string;
  imageUrl: string;
  top: number;
  left: number;
  targetX: number;
  targetY: number;
  animationDelay: string;
  animationDuration: string;
}

export const usePrizeAnimation = () => {
  const [showPrizeAnimation, setShowPrizeAnimation] = useState<boolean>(false);
  const [showDiscountApplied, setShowDiscountApplied] = useState<boolean>(false);
  const [flyingPrizes, setFlyingPrizes] = useState<FlyingPrize[] | null>(null);
  const [flyingDiscounts, setFlyingDiscounts] = useState<FlyingDiscount[] | null>(null);
  const [walletPulse, setWalletPulse] = useState<boolean>(false);
  const [discountPulse, setDiscountPulse] = useState<boolean>(false);
  const walletRef = useRef<HTMLDivElement>(null);

  const triggerPrizeAnimation = useCallback((prizeId: string, imageUrl: string, clickX: number, clickY: number) => {
    const wallet = walletRef.current;
    const walletRect = wallet?.getBoundingClientRect();

    // Get scroll offset from the routes container
    const routesContainer = document.getElementById('routes-container');
    const scrollTop = routesContainer?.scrollTop || 0;

    // Adjust click coordinates to account for scroll
    const adjustedClickY = clickY + scrollTop;

    // Calculate absolute target position (wallet center)
    let walletCenterX = 20;
    let walletCenterY = window.innerHeight - 30; // Default to bottom of screen
    if (walletRect) {
      walletCenterX = walletRect.left + walletRect.width / 2;
      walletCenterY = walletRect.top + walletRect.height / 2;
    }

    // Calculate translation vector from click to wallet center
    // Wallet is fixed positioned, so use viewport coordinates for target calculation
    const targetX = walletCenterX - clickX;
    const targetY = walletCenterY - clickY;

    // Create flying prizes with staggered animations
    const newFlying = [
      {
        key: `${prizeId}-1-${Date.now()}`,
        id: prizeId,
        imageUrl,
        top: adjustedClickY,
        left: clickX,
        targetX,
        targetY,
        animationDelay: '0s',
        animationDuration: '0.6s',
      },
      {
        key: `${prizeId}-2-${Date.now()}`,
        id: prizeId,
        imageUrl,
        top: adjustedClickY + 5,
        left: clickX + 5,
        targetX,
        targetY,
        animationDelay: '0.1s',
        animationDuration: '0.5s',
      },
      {
        key: `${prizeId}-3-${Date.now()}`,
        id: prizeId,
        imageUrl,
        top: adjustedClickY - 5,
        left: clickX - 5,
        targetX,
        targetY,
        animationDelay: '0.2s',
        animationDuration: '0.7s',
      },
    ];

    // Start the animations
    setFlyingPrizes(newFlying);

    // Trigger wallet pulse after the last prize reaches the wallet
    setTimeout(() => {
      setWalletPulse(true);
      setTimeout(() => setWalletPulse(false), 300);
    }, 700); // Slightly after the longest animation duration

    // Show the +3€ animation
    setTimeout(() => {
      setShowPrizeAnimation(true);
      setTimeout(() => setShowPrizeAnimation(false), 1000);
    }, 800); // After wallet pulse

    // Clear flying prizes after all animations complete
    setTimeout(() => setFlyingPrizes(null), 1000);
  }, []);

  const triggerDiscountAnimation = useCallback(() => {
    setShowDiscountApplied(true);
    setTimeout(() => setShowDiscountApplied(false), 3000);
  }, []);

  const triggerWalletToDiscountAnimation = useCallback((discountElementRef: React.RefObject<HTMLElement>) => {
    const wallet = walletRef.current;
    const discountElement = discountElementRef.current;

    if (!wallet || !discountElement) {
      console.warn('Wallet or discount element not found for animation');
      return;
    }

    const walletRect = wallet.getBoundingClientRect();
    const discountRect = discountElement.getBoundingClientRect();

    // Get scroll offset from the routes container
    const routesContainer = document.getElementById('routes-container');
    const scrollTop = routesContainer?.scrollTop || 0;

    // Calculate start position (wallet center)
    const walletCenterX = walletRect.left + walletRect.width / 2;
    const walletCenterY = walletRect.top + walletRect.height / 2;

    // Calculate target position (discount element center)
    const discountCenterX = discountRect.left + discountRect.width / 2;
    const discountCenterY = discountRect.top + discountRect.height / 2 + scrollTop;

    // Calculate translation vector from wallet to discount
    const targetX = discountCenterX - walletCenterX;
    const targetY = discountCenterY - walletCenterY;

    // Create flying discounts with staggered animations
    const newFlyingDiscounts = [
      {
        key: `discount-1-${Date.now()}`,
        id: 'wallet-to-discount',
        imageUrl: diamondIcon,
        top: walletCenterY,
        left: walletCenterX,
        targetX,
        targetY,
        animationDelay: '0s',
        animationDuration: '0.8s',
      },
      {
        key: `discount-2-${Date.now()}`,
        id: 'wallet-to-discount',
        imageUrl: diamondIcon,
        top: walletCenterY + 3,
        left: walletCenterX + 3,
        targetX,
        targetY,
        animationDelay: '0.15s',
        animationDuration: '0.7s',
      },
      {
        key: `discount-3-${Date.now()}`,
        id: 'wallet-to-discount',
        imageUrl: diamondIcon,
        top: walletCenterY - 3,
        left: walletCenterX - 3,
        targetX,
        targetY,
        animationDelay: '0.3s',
        animationDuration: '0.9s',
      },
    ];

    // Start the animations
    setFlyingDiscounts(newFlyingDiscounts);

    // Trigger discount element pulse after the last discount reaches it
    setTimeout(() => {
      setDiscountPulse(true);
      setTimeout(() => setDiscountPulse(false), 400);
    }, 900); // After the longest animation duration

    // Clear flying discounts after all animations complete
    setTimeout(() => setFlyingDiscounts(null), 1200);
  }, []);

  return {
    showPrizeAnimation,
    showDiscountApplied,
    flyingPrizes,
    flyingDiscounts,
    walletPulse,
    discountPulse,
    walletRef,
    triggerPrizeAnimation,
    triggerDiscountAnimation,
    triggerWalletToDiscountAnimation,
  };
};
